import {
  breakpoints,
  Breakpoints,
  colors,
  easingTokens,
  Radius,
  Shadow,
  shadows,
  space,
  transitionTokens,
} from '@ardoq/design-tokens';
import facepaint from 'facepaint';
import { ReactNode } from 'react';
import {
  BackgroundColor,
  BorderColor,
  BorderWidthVariant,
  MarginVariant,
  Overflow,
  Position,
  SpaceVariant,
  TransitionToken,
  ZIndex,
} from './types';

export type ResponsiveConditions<T> = {
  [K in Breakpoints]?: T;
};

export type ResponsiveProp<T extends string | number = string> =
  | T
  | ResponsiveConditions<T>;

type resolveValueFn<T extends string, S extends string> = (
  value: T
) => S | undefined;

export type ResponsiveStyle<T = string> =
  | T
  | [T | null, T | null, T | null, T | null, T | null];

function isResponsiveCondition<T extends string>(
  value: ResponsiveProp<T>
): value is ResponsiveConditions<T> {
  return (
    (value instanceof Object &&
      (value as ResponsiveConditions<T>).mobile !== undefined) ||
    (value as ResponsiveConditions<T>).tablet !== undefined ||
    (value as ResponsiveConditions<T>).desktop !== undefined ||
    (value as ResponsiveConditions<T>).desktopLarge !== undefined ||
    (value as ResponsiveConditions<T>).desktopXLarge !== undefined
  );
}

/**
 * Layers an axis-specific responsive prop over a more general one per
 * breakpoint. A responsive override only replaces the breakpoints it names, so
 * `gap="medium" columnGap={{ desktop: 'large' }}` keeps a medium column gap
 * below desktop instead of leaving those breakpoints with no column gap.
 */
export function mergeResponsiveProps<T extends string>(
  override: ResponsiveProp<T> | undefined,
  base: ResponsiveProp<T> | undefined
): ResponsiveProp<T> | undefined {
  if (override === undefined) return base;
  if (base === undefined) return override;
  if (!isResponsiveCondition(override)) return override;
  if (!isResponsiveCondition(base)) return { mobile: base, ...override };
  return { ...base, ...override };
}

export function resolveResponsiveProp<
  T extends string,
  S extends string = string,
>(
  prop?: ResponsiveProp<T>,
  resolveValue: resolveValueFn<T, S> = s => s as unknown as S
): S | ResponsiveStyle<S> | undefined {
  if (!prop) return undefined;
  if (isResponsiveCondition(prop)) {
    return [
      (prop.mobile && resolveValue(prop.mobile)) || null,
      (prop.tablet && resolveValue(prop.tablet)) || null,
      (prop.desktop && resolveValue(prop.desktop)) || null,
      (prop.desktopLarge && resolveValue(prop.desktopLarge)) || null,
      (prop.desktopXLarge && resolveValue(prop.desktopXLarge)) || null,
    ];
  }
  return resolveValue(prop);
}

export const mq = facepaint([
  `@media(min-width: ${breakpoints.tablet})`,
  `@media(min-width: ${breakpoints.desktop})`,
  `@media(min-width: ${breakpoints.desktopLarge})`,
  `@media(min-width: ${breakpoints.desktopXLarge})`,
]);

export type AccessibilityProps = React.AriaAttributes & {
  /**
   * @see https://www.w3.org/WAI/ARIA/apg/example-index/#examples_by_role_label
   */
  role?: React.AriaRole;
};

/**
 * This list is not finished. If you think some attributes
 * are missing, please create a PR to add them.
 */
type AllowedHTMLAttributes = Pick<
  React.HTMLAttributes<HTMLElement>,
  | 'id'
  | 'className'
  | 'style'
  | 'dangerouslySetInnerHTML'
  | 'onMouseOver'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onFocus'
  | 'onBlur'
  | 'onDragStart'
  | 'onDragOver'
  | 'onDragEnd'
  | 'hidden'
>;

type AsProps = {
  /**
   * For `ul` use the `Stack` component instead
   */
  as?:
    | 'div'
    | 'section'
    | 'main'
    | 'article'
    | 'aside'
    | 'header'
    | 'footer'
    | 'li'
    | 'nav';
};

type BoxBorderColor = BorderColor | 'transparent';

export type CommonBoxProps = {
  padding?: ResponsiveProp<SpaceVariant>;
  paddingLeft?: ResponsiveProp<SpaceVariant>;
  paddingRight?: ResponsiveProp<SpaceVariant>;
  paddingTop?: ResponsiveProp<SpaceVariant>;
  paddingBottom?: ResponsiveProp<SpaceVariant>;
  paddingX?: ResponsiveProp<SpaceVariant>;
  paddingY?: ResponsiveProp<SpaceVariant>;
  margin?: ResponsiveProp<MarginVariant>;
  marginLeft?: ResponsiveProp<MarginVariant>;
  marginRight?: ResponsiveProp<MarginVariant>;
  marginTop?: ResponsiveProp<MarginVariant>;
  marginBottom?: ResponsiveProp<MarginVariant>;
  marginX?: ResponsiveProp<MarginVariant>;
  marginY?: ResponsiveProp<MarginVariant>;
  borderWidth?: BorderWidthVariant;
  borderWidthLeft?: BorderWidthVariant;
  borderWidthRight?: BorderWidthVariant;
  borderWidthTop?: BorderWidthVariant;
  borderWidthBottom?: BorderWidthVariant;
  borderColor?: BoxBorderColor;
  /**
   * Creates a new stacking context if true
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/isolation
   */
  isolate?: boolean;
  position?: ResponsiveProp<Position>;
  children?: ReactNode;
  width?: 'full' | 'fit-content';
  height?: 'full';
  backgroundColor?: BackgroundColor;
  boxShadow?: Shadow;
  borderRadius?: Radius;
  flexGrow?: ResponsiveProp<0 | 1>;
  flexShrink?: ResponsiveProp<0 | 1>;
  maxWidth?: ResponsiveProp<string>;
  minWidth?: ResponsiveProp<string>;
  minHeight?: ResponsiveProp<string>;
  maxHeight?: ResponsiveProp<string>;
  overflow?: Overflow;
  overflowX?: Overflow;
  overflowY?: Overflow;
  zIndex?: ZIndex;
  transition?: TransitionToken;
} & AccessibilityProps &
  AllowedHTMLAttributes &
  AsProps;

export const spaceVariantToValue = (
  spaceVariant?: ResponsiveProp<SpaceVariant>
) => {
  if (!spaceVariant) return undefined;
  return resolveResponsiveProp(spaceVariant, spaceVariant => {
    return space[spaceVariant];
  });
};

const marginVariantToValue = (
  marginVariant?: ResponsiveProp<MarginVariant>
) => {
  if (!marginVariant) return undefined;
  return resolveResponsiveProp(marginVariant, marginVariant => {
    return marginVariant === 'auto' ? 'auto' : space[marginVariant];
  });
};

const widthVariantToValue = (width?: 'full' | 'fit-content') => {
  if (!width) return undefined;
  return width === 'full' ? '100%' : 'fit-content';
};

const resolveBoxShadow = (boxShadow?: Shadow): string | undefined => {
  if (!boxShadow) return undefined;
  return shadows[boxShadow];
};

const borderWidthVariantToValue = (
  borderWidthVariant?: BorderWidthVariant
): string | undefined => {
  if (borderWidthVariant === undefined) return undefined;
  return `${borderWidthVariant}px`;
};

const resolveTransition = (
  transition?: TransitionToken
): string | undefined => {
  if (!transition) return undefined;
  return `all ${transitionTokens[transition]} ${easingTokens.cubicInOut}`;
};

const resolveBorder = (
  borderWidth?: string,
  borderColor: BoxBorderColor = 'borderSubtle'
): string | undefined => {
  if (borderWidth === undefined) return undefined;

  const color =
    borderColor === 'transparent' ? 'transparent' : colors[borderColor];
  return `${borderWidth} solid ${color}`;
};

export const resolveCommonBoxProps = ({
  padding,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  paddingX,
  paddingY,
  margin,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
  marginX,
  marginY,
  borderWidth,
  borderWidthLeft,
  borderWidthRight,
  borderWidthTop,
  borderWidthBottom,
  borderColor,
  isolate,
  position,
  width,
  height,
  backgroundColor,
  boxShadow,
  borderRadius,
  flexGrow,
  flexShrink,
  maxWidth,
  minWidth,
  minHeight,
  maxHeight,
  overflow,
  overflowX,
  overflowY,
  zIndex,
  transition,
}: CommonBoxProps) => {
  const resolvedPaddingLeft = spaceVariantToValue(
    paddingLeft || paddingX || padding
  );
  const resolvedPaddingRight = spaceVariantToValue(
    paddingRight || paddingX || padding
  );
  const resolvedPaddingTop = spaceVariantToValue(
    paddingTop || paddingY || padding
  );
  const resolvedPaddingBottom = spaceVariantToValue(
    paddingBottom || paddingY || padding
  );
  const resolvedMarginLeft = marginVariantToValue(
    marginLeft || marginX || margin
  );
  const resolvedMarginRight = marginVariantToValue(
    marginRight || marginX || margin
  );
  const resolvedMarginTop = marginVariantToValue(
    marginTop || marginY || margin
  );
  const resolvedMarginBottom = marginVariantToValue(
    marginBottom || marginY || margin
  );
  const resolvedBorderLeft = resolveBorder(
    borderWidthVariantToValue(borderWidthLeft ?? borderWidth),
    borderColor
  );
  const resolvedBorderRight = resolveBorder(
    borderWidthVariantToValue(borderWidthRight ?? borderWidth),
    borderColor
  );
  const resolvedBorderTop = resolveBorder(
    borderWidthVariantToValue(borderWidthTop ?? borderWidth),
    borderColor
  );
  const resolvedBorderBottom = resolveBorder(
    borderWidthVariantToValue(borderWidthBottom ?? borderWidth),
    borderColor
  );
  const resolvedWidth = widthVariantToValue(width);
  const resolvedHeight = height === 'full' ? '100%' : undefined;
  const resolvedBoxShadow = resolveBoxShadow(boxShadow);

  return {
    $paddingLeft: resolvedPaddingLeft,
    $paddingRight: resolvedPaddingRight,
    $paddingTop: resolvedPaddingTop,
    $paddingBottom: resolvedPaddingBottom,
    $marginLeft: resolvedMarginLeft,
    $marginRight: resolvedMarginRight,
    $marginTop: resolvedMarginTop,
    $marginBottom: resolvedMarginBottom,
    $borderLeft: resolvedBorderLeft,
    $borderRight: resolvedBorderRight,
    $borderTop: resolvedBorderTop,
    $borderBottom: resolvedBorderBottom,
    $width: resolvedWidth,
    $height: resolvedHeight,
    $backgroundColor: backgroundColor,
    $boxShadow: resolvedBoxShadow,
    $borderRadius: borderRadius,
    $isolate: isolate,
    $position: resolveResponsiveProp<Position, Position>(position),
    $flexGrow: resolveResponsiveProp(flexGrow?.toString()),
    $flexShrink: resolveResponsiveProp(flexShrink?.toString()),
    $maxWidth: resolveResponsiveProp(maxWidth),
    $minWidth: resolveResponsiveProp(minWidth),
    $minHeight: resolveResponsiveProp(minHeight),
    $maxHeight: resolveResponsiveProp(maxHeight),
    $overflow: overflow,
    $overflowX: overflowX,
    $overflowY: overflowY,
    $zIndex: zIndex,
    $transition: resolveTransition(transition),
  };
};
